import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import Card from '../../../components/ui/Card';
import EvolvableCard from '../../../components/ui/EvolvableCard';
import { translateTerm as translateTermUtil, fmtBRL, fmtNum } from '../utils';

const SHAREHOLDER_COLORS = [
  '#10b981', '#3b82f6', '#f59e0b', '#ec4899', '#8b5cf6',
  '#06b6d4', '#f97316', '#84cc16', '#a78bfa', '#fb923c',
];

export default function ShareholdersTab({ lang, year, petrobrasData, refDate, t }) {
  const translateTerm = useCallback((term) => translateTermUtil(term, lang), [lang]);

  const [pieView, setPieView] = useState('ordinary');
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Dismiss tooltip on outside click
  useEffect(() => {
    const handler = () => setActiveTooltip(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const shareholderDistribution = useMemo(() => {
    const pos = petrobrasData['posicao_acionaria'] || [];
    return pos
      .filter(s => s.CNPJ_Companhia === '33.000.167/0001-01' && (!s.Acionista_Relacionado || s.Acionista_Relacionado === ''))
      .sort((a, b) => (parseFloat(b.Percentual_Total_Acoes_Circulacao) || 0) - (parseFloat(a.Percentual_Total_Acoes_Circulacao) || 0));
  }, [petrobrasData]);

  const pieChartsData = useMemo(() => {
    const pos = petrobrasData['posicao_acionaria'] || [];
    const mainHolders = pos.filter(s => s.CNPJ_Companhia === '33.000.167/0001-01' && (!s.Acionista_Relacionado || s.Acionista_Relacionado === ''));

    let totalOrd = 0;
    let totalPref = 0;
    const ordinary = mainHolders.map(s => {
      const v = parseFloat(s.Quantidade_Acao_Ordinaria_Circulacao) || 0;
      totalOrd += v;
      return { name: translateTerm(s.Acionista), value: v };
    }).filter(d => d.value > 0);

    const preferred = mainHolders.map(s => {
      const v = parseFloat(s.Quantidade_Acao_Preferenciais_Circulacao || s.Quantidade_Acao_Preferencial_Circulacao) || 0;
      totalPref += v;
      return { name: translateTerm(s.Acionista), value: v };
    }).filter(d => d.value > 0);

    const totalCapital = [
      { name: lang === 'pt' ? 'Ordinárias (ON)' : 'Common (ON)', value: totalOrd },
      { name: lang === 'pt' ? 'Preferenciais (PN)' : 'Preferred (PN)', value: totalPref },
    ].filter(d => d.value > 0);

    return { ordinary, preferred, totalCapital, totalOrd, totalPref };
  }, [petrobrasData, lang, translateTerm]);

  const getShareholderColor = useCallback((name) => {
    const keys = shareholderDistribution.map(s => translateTerm(s.Acionista));
    const idx = keys.indexOf(name);
    return SHAREHOLDER_COLORS[idx % SHAREHOLDER_COLORS.length];
  }, [shareholderDistribution, translateTerm]);

  const getShareholderHistory = useCallback((_name) => {
    return [];
  }, []);

  const renderSparkline = useCallback((history, currentYear) => {
    if (history.length < 2) return null;
    const max = Math.max(...history.map(h => h.pct));
    const min = Math.min(...history.map(h => h.pct));
    const range = max - min || 1;
    const W = 220; const H = 36;
    const pts = history.map((h, i) => {
      const x = (i / (history.length - 1)) * W;
      const y = H - ((h.pct - min) / range) * H;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    return (
      <div className="mt-2 pt-2 border-t border-[var(--border)]">
        <svg width={W} height={H + 2} viewBox={`0 0 ${W} ${H + 4}`} className="overflow-visible">
          <polyline fill="none" stroke="#10b981" strokeWidth="1.5" points={pts} />
          {history.map((h, i) => {
            const x = (i / (history.length - 1)) * W;
            const y = H - ((h.pct - min) / range) * H;
            return (
              <circle key={i} cx={x} cy={y} r={h.year === currentYear ? 4 : 2}
                fill={h.year === currentYear ? '#34d399' : '#10b981'}
                stroke={h.year === currentYear ? '#0F1016' : 'none'}
                strokeWidth={h.year === currentYear ? 1.5 : 0}
              />
            );
          })}
        </svg>
      </div>
    );
  }, []);

  const CustomPieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    const total = pieView === 'ordinary' ? pieChartsData.totalOrd
      : pieView === 'preferred' ? pieChartsData.totalPref
      : pieChartsData.totalOrd + pieChartsData.totalPref;
    const pct = total > 0 ? (d.value / total * 100).toFixed(2) : '0.00';
    return (
      <div className="bg-[var(--card)]/90 backdrop-blur-md border border-[var(--border)] p-2.5 rounded-xl shadow-xl text-xs space-y-0.5 z-[100]">
        <p className="font-bold text-[var(--text)]">{d.name}</p>
        <p className="text-emerald-400 font-semibold">{fmtNum(d.value)} {lang === 'pt' ? 'ações' : 'shares'}</p>
        <p className="text-yellow-500 font-bold">{pct}%</p>
      </div>
    );
  };

  const handleTooltipEnter = useCallback((e, name) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveTooltip({ name, x: rect.left + rect.width / 2, y: rect.top, pinned: false });
  }, []);
  const handleTooltipLeave = useCallback(() => {
    setActiveTooltip(prev => (prev && prev.pinned ? prev : null));
  }, []);
  const handleTooltipToggle = useCallback((e, name) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setActiveTooltip(prev =>
      prev && prev.name === name && prev.pinned ? null : { name, x: rect.left + rect.width / 2, y: rect.top, pinned: true }
    );
  }, []);

  return (
    <div className="space-y-6">

      {/* Row 1: Major Shareholders & Control Shareholders Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Progress Bar distribution */}
        <Card title={t.card_major_holders} refDate={refDate}>
          <div className="space-y-4">
            {shareholderDistribution.map((s, idx) => {
              const pct = parseFloat(s.Percentual_Total_Acoes_Circulacao) || 0;
              return (
                <div
                  key={idx}
                  onMouseEnter={(e) => handleTooltipEnter(e, translateTerm(s.Acionista))}
                  onMouseLeave={handleTooltipLeave}
                  onClick={(e) => handleTooltipToggle(e, translateTerm(s.Acionista))}
                  className="group p-2 -mx-2 rounded-xl transition-all duration-300 hover:bg-[var(--accent-soft)]/30 hover:translate-x-1 cursor-pointer space-y-2"
                >
                  <div className="flex justify-between text-xs">
                    <span className="font-semibold text-[var(--text)] group-hover:text-emerald-400 transition-colors flex items-center gap-1">
                      {translateTerm(s.Acionista)}
                      <svg className="w-3.5 h-3.5 text-[var(--muted)] opacity-30 group-hover:opacity-100 transition-opacity flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                    </span>
                    <span className="font-bold text-yellow-500 group-hover:scale-110 transform transition-transform">{pct.toFixed(2)}%</span>
                  </div>
                  <div className="w-full bg-[var(--accent-soft)] h-1.5 rounded-full overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-emerald-600 to-emerald-400 h-full rounded-full transform origin-left transition-all duration-300 group-hover:brightness-110 group-hover:scale-y-125"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* EvolvableCard: Ctrl Shareholders Table */}
        <EvolvableCard title={t.card_ctrl_shareholders} petrobrasData={petrobrasData} year={year}>{(localData) => (
          <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
            <table className="w-full text-left border-collapse text-[10px] min-w-[500px]">
              <thead>
                <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                  <th className="p-3 font-semibold">{t.th_holder}</th>
                  <th className="p-3 font-semibold">{t.th_person_type}</th>
                  <th className="p-3 font-semibold">{t.th_ord_shares}</th>
                  <th className="p-3 font-semibold">{t.th_pref_shares}</th>
                  <th className="p-3 font-semibold">{t.th_total_pct}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {(localData['posicao_acionaria'] || [])
                  .filter(s => s.CNPJ_Companhia === '33.000.167/0001-01' && (!s.Acionista_Relacionado || s.Acionista_Relacionado === ''))
                  .map((s, idx) => (
                    <tr
                      key={idx}
                      onMouseEnter={(e) => handleTooltipEnter(e, translateTerm(s.Acionista))}
                      onMouseLeave={handleTooltipLeave}
                      onClick={(e) => handleTooltipToggle(e, translateTerm(s.Acionista))}
                      className="hover:bg-[var(--accent-soft)]/20 group cursor-pointer transition-colors"
                    >
                      <td className="p-3 font-bold text-[var(--text)] group-hover:text-emerald-400 max-w-[120px] transition-colors">
                        <div className="flex items-center gap-1.5 justify-between">
                          <span className="truncate">{translateTerm(s.Acionista)}</span>
                          <svg className="w-3.5 h-3.5 text-[var(--muted)] opacity-30 group-hover:opacity-100 transition-opacity flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                        </div>
                      </td>
                      <td className="p-3 text-[var(--muted)]">{translateTerm(s.Tipo_Pessoa_Acionista)}</td>
                      <td className="p-3 text-[var(--text)]">{fmtNum(s.Quantidade_Acao_Ordinaria_Circulacao)}</td>
                      <td className="p-3 text-[var(--text)]">{fmtNum(s.Quantidade_Acao_Preferenciais_Circulacao || s.Quantidade_Acao_Preferencial_Circulacao)}</td>
                      <td className="p-3 font-bold text-yellow-500">{parseFloat(s.Percentual_Total_Acoes_Circulacao || 0).toFixed(2)}%</td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        )}</EvolvableCard>
      </div>

      {/* Row 2: Pie Charts & Share Rights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Pie Chart Card */}
        <Card
          title={lang === 'pt' ? 'Gráficos de Pizza (Distribuição de Ações)' : 'Stock Share Pie Charts'}
          subtitle={lang === 'pt' ? 'Proporções e detalhes por tipo de ação' : 'Composition breakdown by class of share'}
          refDate={refDate}
        >
          <div className="flex flex-col h-full space-y-4">
            {/* Segmented Controller */}
            <div className="flex bg-[var(--accent-soft)] p-1 rounded-xl w-full border border-[var(--border)] overflow-hidden">
              {[
                { key: 'ordinary', label: lang === 'pt' ? 'Ordinárias (ON)' : 'Common (ON)' },
                { key: 'preferred', label: lang === 'pt' ? 'Preferenciais (PN)' : 'Preferred (PN)' },
                { key: 'totalCapital', label: lang === 'pt' ? 'Composição' : 'Composition' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setPieView(key)}
                  className={`flex-1 text-[10px] sm:text-xs py-1.5 px-2 rounded-lg font-bold transition-all ${
                    pieView === key
                      ? 'bg-[var(--card)] text-emerald-400 shadow-md border border-[var(--border)]'
                      : 'text-[var(--muted)] hover:text-[var(--text)]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Chart & Legend Grid */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
              <div className="col-span-1 md:col-span-5 h-[180px] flex items-center justify-center relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip content={<CustomPieTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                    <Pie
                      data={pieView === 'ordinary' ? pieChartsData.ordinary : pieView === 'preferred' ? pieChartsData.preferred : pieChartsData.totalCapital}
                      cx="50%" cy="50%"
                      innerRadius={45} outerRadius={65} paddingAngle={2} dataKey="value"
                    >
                      {(pieView === 'ordinary' ? pieChartsData.ordinary : pieView === 'preferred' ? pieChartsData.preferred : pieChartsData.totalCapital).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={pieView === 'totalCapital' ? (index === 0 ? '#10b981' : '#06b6d4') : getShareholderColor(entry.name)} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-[9px] font-bold text-[var(--muted)] uppercase tracking-wider">
                    {pieView === 'ordinary' ? 'ON' : pieView === 'preferred' ? 'PN' : 'Total'}
                  </span>
                  <span className="text-xs font-black text-[var(--text)]">
                    {pieView === 'ordinary'
                      ? (pieChartsData.totalOrd > 0 ? '100.0%' : '0%')
                      : pieView === 'preferred'
                        ? '100.0%'
                        : `${((pieChartsData.totalOrd / (pieChartsData.totalOrd + pieChartsData.totalPref)) * 100).toFixed(0)}% ON`}
                  </span>
                </div>
              </div>

              <div className="col-span-1 md:col-span-7 space-y-1.5 max-h-[190px] overflow-y-auto pr-1">
                {(pieView === 'ordinary' ? pieChartsData.ordinary : pieView === 'preferred' ? pieChartsData.preferred : pieChartsData.totalCapital).map((entry, index) => {
                  const total = pieView === 'ordinary' ? pieChartsData.totalOrd : pieView === 'preferred' ? pieChartsData.totalPref : pieChartsData.totalOrd + pieChartsData.totalPref;
                  const pct = total > 0 ? (entry.value / total) * 100 : 0;
                  const color = pieView === 'totalCapital' ? (index === 0 ? '#10b981' : '#06b6d4') : getShareholderColor(entry.name);
                  return (
                    <div key={`legend-${index}`} className="flex items-center justify-between text-[10px] p-1.5 rounded-lg hover:bg-[var(--accent-soft)]/20 transition-all">
                      <div className="flex items-center space-x-2 truncate pr-2">
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-[var(--text)] font-semibold truncate" title={entry.name}>{entry.name}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-right flex-shrink-0">
                        <span className="text-[var(--muted)] text-[8px] font-mono">{fmtNum(entry.value)}</span>
                        <span className="text-yellow-500 font-bold font-mono w-[42px]">{pct.toFixed(2)}%</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Card>

        {/* EvolvableCard: Share Rights */}
        <EvolvableCard title={t.card_share_rights} petrobrasData={petrobrasData} year={year}>{(localData) => (
          <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs min-w-[600px]">
              <thead>
                <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                  <th className="p-3.5 font-semibold">{t.th_share_class}</th>
                  <th className="p-3.5 font-semibold">{t.th_pref_type}</th>
                  <th className="p-3.5 font-semibold">{t.th_tag_along}</th>
                  <th className="p-3.5 font-semibold">{t.th_dividend_rights}</th>
                  <th className="p-3.5 font-semibold">{t.th_voting_rights}</th>
                  <th className="p-3.5 font-semibold">{t.th_redemption}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {(() => {
                  const rightsData = (localData['direito_acao'] || []).length > 0
                    ? localData['direito_acao']
                    : [];
                  const isFallback = false;
                  if (rightsData.length > 0) {
                    return rightsData.map((r, idx) => (
                      <tr key={idx} className="hover:bg-[var(--accent-soft)]/20">
                        <td className="p-3.5 font-bold text-[var(--text)]">
                          {translateTerm(r.Especie_Acao)}
                          {isFallback && (
                            <span className="ml-2 text-[10px] text-yellow-600 dark:text-yellow-400 bg-yellow-500/10 border border-yellow-500/20 px-1.5 py-0.5 rounded font-normal inline-block">
                              {lang === 'pt' ? 'Estatuto 2022' : '2022 Bylaws'}
                            </span>
                          )}
                        </td>
                        <td className="p-3.5 text-[var(--text)]">{translateTerm(r.Classe_Acao_Preferencial)}</td>
                        <td className="p-3.5 font-bold text-yellow-500">{parseFloat(r.Percentual_Tag_Along || 0).toFixed(1)}%</td>
                        <td className="p-3.5 text-[var(--muted)] max-w-xs truncate" title={translateTerm(r.Direito_Dividendo)}>{translateTerm(r.Direito_Dividendo)}</td>
                        <td className="p-3.5 text-[var(--muted)] max-w-xs truncate" title={translateTerm(r.Direito_Voto)}>{translateTerm(r.Direito_Voto)}</td>
                        <td className="p-3.5 text-[var(--muted)] max-w-xs truncate" title={translateTerm(r.Caracteristicas_Reembolso_Capital)}>{translateTerm(r.Caracteristicas_Reembolso_Capital)}</td>
                      </tr>
                    ));
                  }
                  return (
                    <tr><td colSpan="6" className="p-8 text-center text-[var(--muted)]">{lang === 'pt' ? 'Dados não disponíveis para este ano' : 'Data not available for this year'}</td></tr>
                  );
                })()}
              </tbody>
            </table>
          </div>
        )}</EvolvableCard>
      </div>

      {/* Volume & Price */}
      {(() => {
        const volRows = petrobrasData['volume_valor_mobiliario'] || [];
        const volFallback = false;
        if (!volRows.length) return null;

        const periods = [...new Set(volRows.map(r => r.Data_Fim_Exercicio_Social))].sort();
        const latestPeriod = periods[periods.length - 1];
        const latestRows = volRows.filter(r => r.Data_Fim_Exercicio_Social === latestPeriod);
        const quarters = [...new Set(latestRows.map(r => r.Data_Fim_Trimestre))].sort();
        const chartData = quarters.map(q => {
          const onRow = latestRows.find(r => r.Data_Fim_Trimestre === q && r.Especie_Acao === 'Ordinária');
          const pnRow = latestRows.find(r => r.Data_Fim_Trimestre === q && r.Especie_Acao === 'Preferencial');
          return {
            quarter: q.slice(0, 7),
            'ON Vol (B)': onRow ? +(parseFloat(onRow.Valor_Volume_Negociado) / 1e9).toFixed(2) : 0,
            'PN Vol (B)': pnRow ? +(parseFloat(pnRow.Valor_Volume_Negociado) / 1e9).toFixed(2) : 0,
            'ON Médio': onRow ? +parseFloat(onRow.Valor_Cotacao_Media).toFixed(2) : null,
            'PN Médio': pnRow ? +parseFloat(pnRow.Valor_Cotacao_Media).toFixed(2) : null,
          };
        });
        const lastQ = chartData[chartData.length - 1] || {};
        const totalVol = latestRows.reduce((s, r) => s + (parseFloat(r.Valor_Volume_Negociado) || 0), 0);

        return (
          <Card
            title={lang === 'pt' ? 'Volume e Cotação por Trimestre' : 'Quarterly Volume & Price'}
            subtitle={`${latestPeriod} · PETR3/PETR4 · B3`}
            refDate={refDate}
          >
            {volFallback && null}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              {[
                { label: lang === 'pt' ? 'Vol. Total Anual' : 'Annual Total Vol.', value: fmtBRL(totalVol), color: 'text-emerald-500' },
                { label: 'PETR3 Preço Médio', value: lastQ['ON Médio'] ? `R$ ${lastQ['ON Médio']}` : '-', color: 'text-blue-400' },
                { label: 'PETR4 Preço Médio', value: lastQ['PN Médio'] ? `R$ ${lastQ['PN Médio']}` : '-', color: 'text-indigo-400' },
                { label: lang === 'pt' ? 'Trimestres' : 'Quarters', value: String(chartData.length), color: 'text-yellow-500' },
              ].map((kpi, i) => (
                <div key={i} className="bg-[var(--accent-soft)]/30 border border-[var(--border)] rounded-xl p-3 space-y-1">
                  <span className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider block">{kpi.label}</span>
                  <span className={`text-sm font-black block ${kpi.color}`}>{kpi.value}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider mb-2">{lang === 'pt' ? 'Volume Negociado (R$ B)' : 'Trading Volume (R$ B)'}</p>
            <div className="h-44 mb-5">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="quarter" tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} tickFormatter={v => `${v}B`} />
                  <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '10px' }} formatter={(v) => [`R$ ${v}B`]} />
                  <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }} />
                  <Bar dataKey="ON Vol (B)" name="PETR3 (ON)" fill="#10b981" radius={[4,4,0,0]} />
                  <Bar dataKey="PN Vol (B)" name="PETR4 (PN)" fill="#3b82f6" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider mb-2">{lang === 'pt' ? 'Cotação Média Trimestral (R$)' : 'Quarterly Avg Price (R$)'}</p>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                  <XAxis dataKey="quarter" tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} tickFormatter={v => `R$${v}`} />
                  <Tooltip contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '10px' }} formatter={(v) => [`R$ ${v}`]} />
                  <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }} />
                  <Line type="monotone" dataKey="ON Médio" name="PETR3 Médio" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  <Line type="monotone" dataKey="PN Médio" name="PETR4 Médio" stroke="#3b82f6" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        );
      })()}

      {/* Treasury Shares */}
      {(() => {
        const tesRows = petrobrasData['valor_mobiliario_tesouraria_movimentacao'] || [];
        if (!tesRows.length) return null;

        const periods = [...new Set(tesRows.map(r => r.Data_Fim_Exercicio_Social))].sort();
        const latestPeriod = periods[periods.length - 1];
        const latestRows = tesRows.filter(r => r.Data_Fim_Exercicio_Social === latestPeriod);

        return (
          <Card
            title={lang === 'pt' ? 'Ações em Tesouraria — Movimentação' : 'Treasury Shares — Movement'}
            subtitle={latestPeriod}
            refDate={refDate}
          >
            <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs min-w-[650px]">
                <thead>
                  <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Espécie' : 'Type'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Qtd. Inicial' : 'Initial Qty'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Adquirida' : 'Acquired'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Alienada' : 'Sold'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Cancelada' : 'Cancelled'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Qtd. Final' : 'Final Qty'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? '% Circulação' : '% Float'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {latestRows.map((r, idx) => (
                    <tr key={idx} className="hover:bg-[var(--accent-soft)]/20">
                      <td className="p-3">
                        <span className={`text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider border ${
                          r.Especie_Acao === 'Ordinária'
                            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500'
                            : 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400'
                        }`}>
                          {translateTerm(r.Especie_Acao)} ({r.Especie_Acao === 'Ordinária' ? 'ON' : 'PN'})
                        </span>
                      </td>
                      <td className="p-3 text-right text-[var(--muted)]">{fmtNum(r.Quantidade_Inicial)}</td>
                      <td className="p-3 text-right text-emerald-500 font-semibold">{fmtNum(r.Quantidade_Adquirida)}</td>
                      <td className="p-3 text-right text-red-400 font-semibold">{fmtNum(r.Quantidade_Alienada)}</td>
                      <td className="p-3 text-right text-yellow-500 font-semibold">{fmtNum(r.Quantidade_Cancelada)}</td>
                      <td className="p-3 text-right font-bold text-[var(--text)]">{fmtNum(r.Quantidade_Final)}</td>
                      <td className="p-3 text-right text-[var(--muted)]">{parseFloat(r.Percentual_Relacao_Valores_Mobiliarios_Circulacao || 0).toFixed(4)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })()}

      {/* Fixed-position tooltip overlay */}
      {activeTooltip && (() => {
        const history = getShareholderHistory(activeTooltip.name);
        return (
          <div
            className="fixed bg-[var(--card)] border border-[var(--border)] rounded-xl p-3 shadow-2xl z-50 w-64 text-[10px] pointer-events-none"
            style={{ left: `${activeTooltip.x}px`, top: `${activeTooltip.y}px`, transform: 'translate(-50%, -100%)' }}
          >
            <div className="font-bold text-[var(--text)] mb-2 text-center border-b border-[var(--border)] pb-1.5 flex items-center justify-between">
              <span>{lang === 'pt' ? 'Histórico de Participação' : 'Ownership History'}</span>
              <span className="text-emerald-500 font-mono text-[9px]">2015-2025</span>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono">
              {history.map((h, hIdx) => (
                <div
                  key={hIdx}
                  className={`flex justify-between py-0.5 px-1 rounded transition-all duration-150 ${
                    h.year === year
                      ? 'bg-emerald-500/20 text-emerald-400 font-bold border border-emerald-500/30 scale-105'
                      : 'text-[var(--muted)]'
                  }`}
                >
                  <span>{h.year}:</span>
                  <span>{h.pct.toFixed(2)}%</span>
                </div>
              ))}
            </div>
            {renderSparkline(history, year)}
          </div>
        );
      })()}

    </div>
  );
}
